import { useState, useCallback, useEffect } from 'react';
import * as authApi from '../api/auth.js';
import * as patientsApi from '../api/patients.js';

const STORAGE_KEY = 'iex_tokens';

function loadTokens() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveTokens(tokens) {
  if (tokens) localStorage.setItem(STORAGE_KEY, JSON.stringify(tokens));
  else localStorage.removeItem(STORAGE_KEY);
}

export function useAuth() {
  const [tokens, setTokens] = useState(loadTokens);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const isAuthed = !!tokens?.access;

  const _setAndPersist = (t) => {
    setTokens(t);
    saveTokens(t);
  };

  // Escuta o evento global de 401 (disparado pelo interceptor do axios)
  useEffect(() => {
    const handle = () => _setAndPersist(null);
    window.addEventListener('iex:unauthorized', handle);
    return () => window.removeEventListener('iex:unauthorized', handle);
  }, []);

  const login = useCallback(async (email, password) => {
    setLoading(true);
    setError(null);
    try {
      const data = await authApi.login(email, password);
      _setAndPersist({ access: data.access_token, refresh: data.refresh_token });
      return data;
    } catch (e) {
      const status = e.response?.status;
      // Mensagem genérica (não revela se o e-mail existe) para credenciais inválidas.
      const msg = status === 401
        ? 'O e-mail e/ou a senha informados não estão cadastrados.'
        : (e.response?.data?.detail ?? 'Não foi possível entrar. Tente novamente.');
      setError(msg);
      throw new Error(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  const signup = useCallback(async (form) => {
    setLoading(true);
    setError(null);
    try {
      // Passo 1: identidade no iam. Se o e-mail já existir (409), é uma
      // conta iam de outro contexto (ex.: funcionário de parceiro) — quem
      // chama decide se orienta para login em vez de tentar de novo aqui.
      const identity = await authApi.registerIdentity({
        email: form.email,
        name: form.name.trim(),
        password: form.password,
      });
      _setAndPersist({ access: identity.access_token, refresh: identity.refresh_token });

      // Passo 2: dados de paciente + consentimentos, já autenticado com o
      // token do passo 1. Se falhar (ex.: CPF duplicado), os tokens acima
      // já ficam salvos — a conta iam existe e pode logar depois para
      // tentar completar o cadastro de novo, sem perder o passo 1.
      const patient = await patientsApi.completeRegistration({
        cpf: form.cpf,
        firstName: form.name.trim().split(/\s+/)[0],
        lastName: form.name.trim().split(/\s+/).slice(1).join(' '),
        phone: form.phone,
        acceptTerms: true,
        acceptPrivacy: true,
      });
      return patient;
    } catch (e) {
      const status = e.response?.status;
      const msg = status === 409
        ? (e.response?.data?.detail ?? 'Este e-mail já tem uma conta. Faça login.')
        : (e.response?.data?.detail ?? 'Não foi possível criar a conta.');
      setError(msg);
      throw new Error(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    // Best-effort: invalida a sessão no iam, mas o logout local nunca deve
    // ficar preso esperando rede (ou falhar se ela cair).
    authApi.logout().catch(() => {});
    _setAndPersist(null);
  }, []);

  return { isAuthed, tokens, loading, error, login, signup, logout };
}
